import React, { useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Rewards from '@nykaa/cart-shared/wrapper/RewardPoints';
import { deviceTypes, ORIGIN_SOURCE } from '@nykaa/cart-shared/constants';
import { IRewardPointsParams } from '@nykaa/cart-shared/store/types/requestParams';
import {
  rewardPointsApplied,
  rewardPointsRemoved,
} from '@nykaa/cart-shared/store/action';
import RewardPointsV2 from '@nykaa/cart-shared/wrapper/RewardPointsV2';
import { EDD_EXPERIEMENT_HEADER } from '@nykaa/cart-shared/store/constants';
import { State } from 'store/types';

interface Props {
  isConvenienceFeeEnabled?: boolean;
  collapsedPriceDetailsEnabled?: boolean;
}
const RewardPoints = ({
  isConvenienceFeeEnabled,
  collapsedPriceDetailsEnabled,
}: Props) => {
  const dispatch = useDispatch();
  const [loadingState, setLoadingState] = useState(false);
  const { rewardsDTO, pincode } = useSelector(
    (state: State) => state.cartReducer.cartInfo
  );
  const isRedemptionRestricted =
    useSelector(
      (state: State) =>
        state.remoteConfig.switches?.rewardPointRedemptionRestriction
    ) || false;
  const eddEnabled =
    useSelector((state: State) => state.remoteConfig.switches?.cartEddWidget) ||
    false;

  const isRewardsPointsDisabled = useMemo(
    () =>
      isRedemptionRestricted &&
      rewardsDTO?.rewardsRedeemable === 0 &&
      Boolean(rewardsDTO?.rewardRestrictionMessage),
    [isRedemptionRestricted, rewardsDTO]
  );

  const handleRewards = useCallback(async () => {
    if (isRewardsPointsDisabled) {
      // fire tracking in this case;
      return;
    }
    setLoadingState(true);

    let params: IRewardPointsParams = {
      deviceType: deviceTypes.DESKTOP,
      originSource: ORIGIN_SOURCE.CART,
      headers: { [EDD_EXPERIEMENT_HEADER]: eddEnabled },
      pincode,
      domain: __PLATFORM__,
      catalog_tag_filter: __PLATFORM__,
    };

    try {
      if (rewardsDTO?.rewardsApplied) {
        await rewardPointsRemoved(params, isConvenienceFeeEnabled)(dispatch);
      } else {
        params = { ...params, used: rewardsDTO?.rewardsAvailable };
        await rewardPointsApplied(params, isConvenienceFeeEnabled)(dispatch);
      }
    } catch (err) {
      console.error('Error handling reward points:', err);
    } finally {
      setLoadingState(false);
    }
  }, [
    isRewardsPointsDisabled,
    eddEnabled,
    rewardsDTO,
    pincode,
    dispatch,
    isConvenienceFeeEnabled,
  ]);

  return collapsedPriceDetailsEnabled ? (
    <RewardPointsV2
      rewardsDTO={rewardsDTO}
      handleRewardPoints={handleRewards}
    />
  ) : (
    <Rewards
      rewardsDTO={rewardsDTO}
      isRedemptionRestricted={isRedemptionRestricted}
      handleRewardPoints={handleRewards}
      loading={loadingState}
    />
  );
};

export default RewardPoints;
